import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { createHandlerChainRunner, Handler, HasOnError, HasOnSuccess } from "@insite/client-framework/HandlerCreator";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import loadProducts from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { ProductModel, QuoteLineModel } from "@insite/client-framework/Types/ApiModels";
import { GetQuoteToCartApiParameter, getQuoteToCartCollection } from "@store/Services/QuoteToCartService";
import addProductFromQuote from "@store/Store/Data/QuoteToQuickOrder/Handlers/AddProductFromQuote";
import { StoreHandlerProps } from "@store/Store/HandlerCreator";
import { QuoteLineToCartModel, QuoteToCartModel } from "@store/Types/QuoteToCart";

export interface QuoteToQuickOrderResult {
    quote?: QuoteToCartModel;
    nbTotal?: number;
    nbAdded?: number;
}

type Parameter = {
    apiParameter: GetQuoteToCartApiParameter;
    onSegmentComplete?: (step:string, nbComplete?:number, nbProcessed?:number, nbTotal?:number) => void;
} & HasOnSuccess<QuoteToQuickOrderResult> &
    HasOnError<any>;
type Props = StoreHandlerProps & {
    apiParameter: GetQuoteToCartApiParameter;
    apiResult: QuoteToQuickOrderResult;
};

type HandlerType = Handler<Parameter, Props>;

export const PopulateApiParameter: HandlerType = props => {
    const { onError, ...parameter } = props.parameter;
    props.apiParameter = props.parameter.apiParameter;
};

export const DispatchBeginLoadQuoteToQuickOrder: HandlerType = props => {
    props.dispatch({
        type: "Store/Data/QuoteToCart/BeginLoadQuote",
        parameter: props.apiParameter,
    });

    props.apiResult = {};
};
export const RequestDataFromApi: HandlerType = async props => {
    props.apiResult.quote = await getQuoteToCartCollection(props.apiParameter);
};

export const AddAllToQuickOrder: HandlerType = async props => {
    if (props.apiResult.quote?.quoteLines?.length) {
        const size = 24;
        let allProductsAdded = false;
        let allProductsFound:ProductModel[] = [];
        
        const quoteLines = props.apiResult.quote?.quoteLines;
        const productIds = quoteLines.map(x => x.productId) as string[]; // Typing necessary, we filter out null values but the linter does not care
    
        props.parameter.onSegmentComplete?.("ImportQuote_ProductsLoading");

        for (let i = 0; i < productIds.length; i += size) {
            let loadedProducts = false;
            const productIdsChunk = productIds.slice(i, i + size);
            let products:ProductModel[] | null = [];

            props.dispatch(
                loadProducts({
                    productIds: productIdsChunk,
                    pageSize: productIdsChunk.length,
                    onComplete: (result) => {
                        products = result.apiResult?.products || null;
                        if (products) {
                            allProductsFound = allProductsFound.concat(products);
                        }
                        loadedProducts = true;
                    },
                }),
            );
            await waitFor(() => loadedProducts === true);
        }

        props.dispatch(
            addProductFromQuote({
                productIds: allProductsFound.map(p => p.id),
                quoteLines,
                onComplete: () => {
                    allProductsAdded = true;
                },
                onSegmentComplete: (step, nbComplete, nbProcessed, nbTotal) => {
                    props.parameter.onSegmentComplete?.(step, nbComplete, nbProcessed, nbTotal);
                },
            })
        );
        await waitFor(() => allProductsAdded === true);

        props.apiResult.nbTotal = quoteLines.length;
        props.apiResult.nbAdded = allProductsFound.length;
    }
};

export const DispatchCompleteLoadQuoteToQuickOrder: HandlerType = props => {
    props.dispatch({
        type: "Store/Data/QuoteToCart/CompleteLoadQuote",
        parameter: props.apiParameter,
    });
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (props.apiResult.quote?.quoteLines.length) {
        props.parameter.onSuccess?.(props.apiResult);
    }
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (!props.apiResult.quote?.quoteLines.length) {
        props.parameter.onError?.(props.apiResult);
    }
};

export const chain = [
    PopulateApiParameter,
    DispatchBeginLoadQuoteToQuickOrder,
    RequestDataFromApi,
    AddAllToQuickOrder,
    DispatchCompleteLoadQuoteToQuickOrder,
    ExecuteOnSuccessCallback,
    ExecuteOnErrorCallback,
];

const loadQuoteToQuickOrder = createHandlerChainRunner(chain, "LoadQuoteToQuickOrder");
export default loadQuoteToQuickOrder;
